import type { UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";
import swLog from "@similarweb/sw-log";

import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

const fetchService = DefaultFetchService.getInstance();

const REQUEST_ACCESS_URL = "api/homepage/invitations/invite/request-access";
const INVITE_TO_REGISTER_URL = "api/homepage/invitations/invite-to-register";

const useInviteUsersToRegister = () => {
    return useMutation({
        mutationFn: async ({ fullName, emails }: { fullName: string; emails: string[] }): Promise<string[]> => {
            try {
                const response = await fetchService.post(INVITE_TO_REGISTER_URL, {
                    FullName: fullName,
                    Emails: emails,
                });

                allTrackers.dispatchTrackEvent("InviteUsersV2", "Send Request", `${emails.length} emails`);
                return Array.isArray(response) ? response : [];
            } catch (error) {
                Sentry.errorTracker({
                    message: "HomePage - inviteUsersToRegister Promise failed",
                    e: error,
                    level: "error",
                    params: {
                        extra: {
                            originalEmails: emails,
                            distinctEmails: emails,
                        },
                    },
                });
                throw error;
            }
        },
    });
};

export const useAllowedDomains = (options = {}): UseQueryResult<string[]> => {
    return useQuery({
        queryKey: ["allowedDomains"],
        queryFn: async (): Promise<string[]> => {
            try {
                const response = await fetchService.get<string[]>("api/homepage/invitations/get-email-domains");
                return response;
            } catch (error) {
                swLog.error("HomePage - getAllowedDomains Promise failed", error);
                throw error;
            }
        },
        initialData: [],

        ...options,
    });
};

interface IAvailableAllocationsResponse {
    LicencesCount: number;
    AvailableAllocations: number;
    IsUnlimited: boolean;
}

export const useTotalSeats = (options = {}): UseQueryResult<IAvailableAllocationsResponse> => {
    return useQuery({
        queryKey: ["totalSeats"],
        queryFn: async (): Promise<IAvailableAllocationsResponse> => {
            try {
                const response = await fetchService.get<IAvailableAllocationsResponse>(
                    "api/homepage/invitations/get-available-allocations",
                );
                return response;
            } catch (error) {
                swLog.error("HomePage - getTotalSeats Promise failed", error);
                throw error;
            }
        },
        initialData: {
            LicencesCount: 0,
            AvailableAllocations: 0,
            IsUnlimited: false,
        },
        ...options,
    });
};

export const validateEmail = (
    email: string,
    allowedDomains: string[],
): { valid: boolean; invalidDomain: boolean; invalidEmail: boolean } => {
    // validate email and ensure that it matches one of the allowed domains
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
        return {
            valid: false,
            invalidDomain: false,
            invalidEmail: true,
        };
    }

    if (allowedDomains?.length > 0) {
        const domain = email.split("@")[1];
        const isInvalidDomain = !allowedDomains.includes(domain);

        if (isInvalidDomain) {
            allTrackers.dispatchTrackEvent("InviteUsersV2", "Email Domain error", email);
        }
        return {
            valid: !isInvalidDomain,
            invalidDomain: isInvalidDomain,
            invalidEmail: false,
        };
    }

    return {
        valid: true,
        invalidDomain: false,
        invalidEmail: false,
    };
};

const requestAccess = async (fullName: string, emails: string[], originalEmails: string[]): Promise<unknown> => {
    const origEmailsStr = originalEmails.join(", ");
    const emailsStr = emails.join(", ");
    try {
        const response = await fetchService.post(REQUEST_ACCESS_URL, {
            Emails: emails,
        });

        await swLog.serverLogger(
            `[JOURNEY] - HomePage - requestAccess Promise success - ${fullName} - originalEmails: ${origEmailsStr} - distinctEmails: ${emailsStr}`,
            null,
            "Info",
        );
        return response;
    } catch (error) {
        const logMsg = `[JOURNEY] - HomePage - requestAccess Promise failed - ${error} - originalEmails: ${origEmailsStr} - distinctEmails: ${emailsStr}`;

        await swLog.serverLogger(logMsg, error, "Error");
        Sentry.errorTracker({
            message: "[JOURNEY] - HomePage - requestAccess Promise failed",
            e: error,
            level: "error",
            params: {
                extra: {
                    originalEmails,
                    distinctEmails: emails,
                },
            },
        });

        throw error;
    }
};

export const useRequestAccess = () => {
    return useMutation({
        mutationFn: async ({
            fullName,
            emails,
            originalEmails,
        }: {
            fullName: string;
            emails: string[];
            originalEmails?: string[];
        }): Promise<unknown> => requestAccess(fullName, emails, originalEmails ?? emails),
    });
};
